import React from 'react';
import {
  Box,
  TextField,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  useTheme,
  alpha,
  Typography
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

export interface DashboardFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  /** Called with explicit dates when triggered by a preset, or no args when triggered by the Apply button. */
  onApplyFilters: (start?: string, end?: string) => void;
}

const toDateString = (d: Date) => d.toISOString().split('T')[0];

const PRESETS = [
  {
    label: 'Today',
    getRange: () => {
      const today = toDateString(new Date());
      return { start: today, end: today };
    },
  },
  {
    label: 'This Week',
    getRange: () => {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
      return { start: toDateString(monday), end: toDateString(today) };
    },
  },
  {
    label: 'This Month',
    getRange: () => {
      const today = new Date();
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: toDateString(first), end: toDateString(today) };
    },
  },
  {
    label: 'Last 30 Days',
    getRange: () => {
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 29);
      return { start: toDateString(past), end: toDateString(today) };
    },
  },
];

const DashboardFilters: React.FC<DashboardFilterProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
}) => {
  const theme = useTheme();

  const dateError =
    startDate && endDate && new Date(endDate) < new Date(startDate)
      ? 'End date must be after start date'
      : '';

  const handlePreset = (preset: (typeof PRESETS)[number]) => {
    const { start, end } = preset.getRange();
    onStartDateChange(start);
    onEndDateChange(end);
    // Pass dates directly so the parent doesn't read stale state
    onApplyFilters(start, end);
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          {/* Quick preset buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, pl: 0.5 }}>
              Quick Select
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              {PRESETS.map((preset) => {
                const { start, end } = preset.getRange();
                const isActive = startDate === start && endDate === end;
                return (
                  <Button
                    key={preset.label}
                    onClick={() => handlePreset(preset)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: isActive ? 700 : 400,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      borderColor: isActive
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.3),
                      color: isActive ? theme.palette.primary.main : 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          </Box>

          {/* Date pickers */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, pl: 0.5 }}>
                From
              </Typography>
              <TextField
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                size="small"
                InputProps={{ startAdornment: <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} /> }}
                sx={{ width: 160 }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, pl: 0.5 }}>
                To
              </Typography>
              <TextField
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                size="small"
                error={!!dateError}
                helperText={dateError}
                InputProps={{ startAdornment: <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} /> }}
                sx={{ width: 160 }}
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={() => onApplyFilters()}
              disabled={!!dateError}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DashboardFilters;
